import React, { ReactElement, useState, ChangeEvent } from 'react';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import GET_CLASSROOM from 'graph/classroom/get-classroom-query';
import { useQuery } from '@apollo/client';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import PrintModal from '../../components/PrintModal';
import {
  ProductFamily,
  ClassroomLicenseUsage,
  ProductType,
  StudentLicenseUsage
} from 'types/schema-generated-types';
import Dropdown, { DropDownOptions } from 'components/@common/Inputs/Dropdown';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';
import { buildSearchQueryParams } from 'utilities/query-params-util';
import PageContainer from 'components/@layout/PageContainer';
import useProductCache from 'store/cache/product-cache';
import { getProductNameAndIcon } from 'utilities/product-utils';

const getProductFamiliesByUsage = (
  licenseUsage: Array<StudentLicenseUsage>,
  productFamiliesCache: ProductFamily[]
): Array<ProductFamily> =>
  licenseUsage.reduce((families: Array<ProductFamily>, license: StudentLicenseUsage) => {
    const createdFamily = families.find(family => family.id === license.product.family.id);
    if (!createdFamily) {
      return [
        ...families,
        productFamiliesCache.find(family => family.code === license.product.family.code) ||
          license.product.family
      ];
    }
    return families;
  }, []);

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      display: 'flex',
      alignItems: 'center'
    },
    productImage: {
      height: 26,
      width: 26,
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1)
    },
    productText: {
      fontWeight: theme.typography.fontWeightBold
    }
  };
});

const handleProductFamily = (classes: any) => (productFamily: ProductFamily): DropDownOptions => {
  const { iconUrl, name } = getProductNameAndIcon(productFamily, ProductType.STUDENT);
  return {
    label: (
      <div className={classes.container}>
        <img
          key={`${productFamily.id}_family`}
          src={iconUrl || ''}
          alt={name || ''}
          className={classes.productImage}
        />
        <span className={classes.productText}>{name}</span>
      </div>
    ),
    value: productFamily.code
  };
};

const PrintStudentLoginCardsModal = (): ReactElement | null => {
  const classes = useStyles();
  const { t } = useTranslation('PrintStudentLoginCardsModal');
  const [{ getPliProductFamilies }] = useProductCache();
  const { classroomId } = useParseActionRouteParams<{ classroomId: string }>();

  const [selected, setSelected] = useState('');
  const { data, loading, error } = useQuery(GET_CLASSROOM, {
    variables: { id: +classroomId }
  });
  const productFamilies = getProductFamiliesByUsage(
    data?.classroom.licenseUsage || ([] as Array<ClassroomLicenseUsage>),
    getPliProductFamilies()
  );

  const handleChange = (evt: ChangeEvent<any>): void => {
    setSelected(evt.target?.value || '');
  };

  const handleOnPrint = (): void => {
    window.open(
      `/print-screen/login-cards${buildSearchQueryParams({
        classroomId: +classroomId,
        product: selected
      })}`
    );
  };

  return (
    <PageContainer loading={loading} error={error}>
      <PrintModal
        title={t('Print Student Login Cards')}
        onPrint={handleOnPrint}
        options={{ printText: t('Print Login Cards'), disablePrintButton: !selected }}
      >
        {productFamilies.length > 0 ? (
          <Dropdown
            id="print-login-cards"
            options={productFamilies.map(handleProductFamily(classes))}
            handleChange={handleChange}
            label={t('Select Application')}
            value={selected}
          />
        ) : (
          <EmptyResultSetMessage
            style={{ marginTop: '12px' }}
            message={t('No product assigned.')}
            subtitle={t(`The classroom doesn't have any product licenses assigned.`)}
          />
        )}
      </PrintModal>
    </PageContainer>
  );
};

export default PrintStudentLoginCardsModal;
